import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface JobPdfData {
  jobNumber: string;
  title: string;
  status: string;
  client: { name: string; email: string; phone: string };
  address: string;
  city?: string;
  province?: string;
  postalCode?: string;
  startDate: string;
  estimatedEndDate: string;
  actualEndDate?: string;
  projectManager: { name: string };
  budget: number;
  spent: number;
  description?: string;
}

const OCHRE = '#C77D3F';
const DARK = '#2C1810';

function fmt(n: number) {
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(n);
}

function fmtDate(d: string | undefined) {
  if (!d) return 'N/A';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return 'N/A';
  return dt.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
}

export async function exportJobToPdf(job: JobPdfData): Promise<void> {
  const variance = job.budget - job.spent;
  const pct = job.budget > 0 ? Math.round((job.spent / job.budget) * 100) : 0;
  const varColor = variance >= 0 ? '#5C7F4F' : '#B8462E';
  const progressWidth = Math.min(pct, 100);
  const progressColor = pct > 100 ? '#B8462E' : pct > 85 ? '#D69E2E' : OCHRE;
  const fullAddress = [job.address, job.city, job.province, job.postalCode].filter(Boolean).join(', ');

  const statusColors: Record<string, string> = {
    completed: '#5C7F4F',
    'in-progress': '#C77D3F',
    scheduled: '#2563EB',
    cancelled: '#B8462E',
    'on-hold': '#D69E2E',
  };
  const statusBg = statusColors[job.status.toLowerCase()] ?? '#6B7280';
  const fontStack = `-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif`;

  const html = `
    <div style="font-family:${fontStack};background:#FAF6F0;color:${DARK};width:794px;">
      <div style="background:${OCHRE};padding:32px 40px 28px;color:#fff;">
        <div style="font-size:12px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;opacity:0.85;margin-bottom:12px;">
          OchrePoint Contracting LTD · Job Summary
        </div>
        <div style="font-size:24px;font-weight:800;line-height:1.15;margin-bottom:8px;">${job.title}</div>
        <div style="display:flex;align-items:center;gap:14px;font-size:13px;opacity:0.9;">
          <span>${job.jobNumber}</span>
          <span>·</span>
          <span style="background:${statusBg};color:#fff;padding:3px 10px;border-radius:12px;font-size:11px;font-weight:700;text-transform:capitalize;">
            ${job.status}
          </span>
        </div>
      </div>

      <div style="padding:32px 40px;">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:20px;">

          <div style="background:#fff;border-radius:10px;padding:18px 20px;border:1px solid #E7DDD1;">
            <div style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.1em;color:${OCHRE};margin-bottom:12px;">Client</div>
            <div style="font-size:14px;font-weight:700;margin-bottom:4px;">${job.client.name || 'N/A'}</div>
            ${job.client.email ? `<div style="font-size:12px;color:#6B7280;margin-bottom:2px;">${job.client.email}</div>` : ''}
            ${job.client.phone ? `<div style="font-size:12px;color:#6B7280;">${job.client.phone}</div>` : ''}
          </div>

          <div style="background:#fff;border-radius:10px;padding:18px 20px;border:1px solid #E7DDD1;">
            <div style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.1em;color:${OCHRE};margin-bottom:12px;">Site Address</div>
            <div style="font-size:13px;font-weight:600;line-height:1.5;">${fullAddress || 'N/A'}</div>
          </div>

          <div style="background:#fff;border-radius:10px;padding:18px 20px;border:1px solid #E7DDD1;">
            <div style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.1em;color:${OCHRE};margin-bottom:12px;">Key Dates</div>
            <table style="border-collapse:collapse;width:100%;">
              <tr>
                <td style="padding:4px 12px 4px 0;color:#6B7280;font-size:11px;white-space:nowrap;">Start</td>
                <td style="padding:4px 0;font-size:12px;font-weight:600;">${fmtDate(job.startDate)}</td>
              </tr>
              <tr>
                <td style="padding:4px 12px 4px 0;color:#6B7280;font-size:11px;white-space:nowrap;">Est. End</td>
                <td style="padding:4px 0;font-size:12px;font-weight:600;">${fmtDate(job.estimatedEndDate)}</td>
              </tr>
              ${job.actualEndDate ? `
              <tr>
                <td style="padding:4px 12px 4px 0;color:#6B7280;font-size:11px;white-space:nowrap;">Actual End</td>
                <td style="padding:4px 0;font-size:12px;font-weight:600;">${fmtDate(job.actualEndDate)}</td>
              </tr>` : ''}
            </table>
          </div>

          <div style="background:#fff;border-radius:10px;padding:18px 20px;border:1px solid #E7DDD1;">
            <div style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.1em;color:${OCHRE};margin-bottom:12px;">Project Manager</div>
            <div style="font-size:15px;font-weight:700;">${job.projectManager.name || 'Unassigned'}</div>
          </div>
        </div>

        <div style="background:#fff;border-radius:10px;padding:18px 20px;border:1px solid #E7DDD1;margin-bottom:20px;">
          <div style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.1em;color:${OCHRE};margin-bottom:16px;">Financial Summary</div>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:16px;">
            <div style="text-align:center;">
              <div style="font-size:10px;color:#6B7280;text-transform:uppercase;font-weight:700;margin-bottom:4px;">Budget</div>
              <div style="font-size:18px;font-weight:800;color:${DARK};">${fmt(job.budget)}</div>
            </div>
            <div style="text-align:center;">
              <div style="font-size:10px;color:#6B7280;text-transform:uppercase;font-weight:700;margin-bottom:4px;">Spent</div>
              <div style="font-size:18px;font-weight:800;color:${OCHRE};">${fmt(job.spent)}</div>
            </div>
            <div style="text-align:center;">
              <div style="font-size:10px;color:#6B7280;text-transform:uppercase;font-weight:700;margin-bottom:4px;">Remaining</div>
              <div style="font-size:18px;font-weight:800;color:${varColor};">${fmt(variance)}</div>
            </div>
          </div>
          <div style="display:flex;justify-content:space-between;font-size:11px;color:#6B7280;margin-bottom:4px;">
            <span>Budget utilization</span>
            <span style="font-weight:700;color:${progressColor};">${pct}%</span>
          </div>
          <div style="height:8px;background:#F3EDE4;border-radius:4px;overflow:hidden;">
            <div style="width:${progressWidth}%;height:100%;background:${progressColor};border-radius:4px;"></div>
          </div>
        </div>

        ${job.description ? `
        <div style="background:#fff;border-radius:10px;padding:18px 20px;border:1px solid #E7DDD1;margin-bottom:20px;">
          <div style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.1em;color:${OCHRE};margin-bottom:12px;">Description</div>
          <p style="font-size:13px;line-height:1.6;color:#4B5563;">${job.description}</p>
        </div>` : ''}
      </div>

      <div style="padding:16px 40px;border-top:1px solid #E7DDD1;display:flex;justify-content:space-between;font-size:11px;color:#9CA3AF;">
        <span>OchrePoint Contracting LTD · Built on Skill, Finished with Pride</span>
        <span>Generated ${new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
      </div>
    </div>
  `;

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = '794px';
  container.style.backgroundColor = '#FAF6F0';
  container.style.zIndex = '-1';
  container.innerHTML = html;
  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#FAF6F0',
      windowWidth: 794,
    });

    const imgWidth = 210;
    const pageHeight = 297;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgData = canvas.toDataURL('image/png');

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(`job-${job.jobNumber.replace('#', '')}-${new Date().toISOString().split('T')[0]}.pdf`);
  } finally {
    document.body.removeChild(container);
  }
}
